<div class="tile-padding-first tile-padding admin-page">

  <!-- Check access: why a user can or cannot use a page. The API answers 404 while the switch is off. -->
  <section class="admin-section tw:mb-8" data-test="access-check" *appFeatureSwitch="featureKeys.accessCheck">
    <h2 class="admin-h2">Check access</h2>
    <p class="admin-lede">Whether a user can use a page, and each gate that decides it: the user, their role, the feature grant and the switch.</p>

    <form class="filter-bar tw:mb-4" (ngSubmit)="checkAccess()">
      <!-- The user box suggests matching accounts as it is typed into (a combobox with a listbox of options). -->
      <div class="filter-bar__field">
        <label class="field__label" for="accessCheckUser">User name or email</label>
        <app-typeahead inputId="accessCheckUser" name="accessCheckUser" inputTestId="access-check-email"
          listTestId="access-check-suggestions" optionTestId="access-check-suggestion" listLabel="Matching accounts"
          [source]="searchUsers" [displayWith]="suggestionUserName"
          [ngModel]="checkUser" (ngModelChange)="onUserInput($event)" (picked)="chooseSuggestion($event)">
          <ng-template let-suggestion>
            <span class="tw:block tw:font-bold">{{suggestion.userName}}</span>
            <span class="tw:block tw:text-xs tw:text-gray-600">
              {{suggestion.name}}@if (suggestion.name && suggestion.email) { · }{{suggestion.email}}
            </span>
            <span class="tw:block tw:text-xs tw:text-gray-600">
              Last sign-in {{suggestion.lastSignInUtc ? (suggestion.lastSignInUtc | date:'d MMM y, HH:mm') : 'never'}}
            </span>
          </ng-template>
        </app-typeahead>
      </div>

      <div class="filter-bar__field">
        <label class="field__label" for="accessCheckPage">Page</label>
        <select id="accessCheckPage" name="accessCheckPage" class="field__control" data-test="access-check-page"
          [ngModel]="checkPage" (ngModelChange)="onCheckPageChange($event)">
          <optgroup label="Pages">
            @for (gate of menuPages; track gate.label) {
              <option [value]="gate.label">{{gate.label}}</option>
            }
          </optgroup>
          <optgroup label="Admin pages">
            @for (gate of routePages; track gate.label) {
              <option [value]="gate.label">{{gate.label}}</option>
            }
          </optgroup>
          <option [value]="otherPage">Other</option>
        </select>
      </div>

      @if (checkPage === otherPage) {
        <div class="filter-bar__field">
          <label class="field__label" for="accessCheckFeature">Feature</label>
          <select id="accessCheckFeature" name="accessCheckFeature" class="field__control"
            data-test="access-check-feature" [(ngModel)]="otherFeature">
            <option value="">(don't check a feature)</option>
            @for (feature of featureOptions; track feature.featureId) {
              <option [value]="feature.description">{{feature.description}}</option>
            }
          </select>
        </div>
        <div class="filter-bar__field">
          <label class="field__label" for="accessCheckSwitch">Switch</label>
          @if (switchListFailed) {
            <input id="accessCheckSwitch" name="accessCheckSwitch" type="text" class="field__control" placeholder="e.g. admin.email-history"
              data-test="access-check-switch" [(ngModel)]="otherSwitchKey" />
          } @else {
            <select id="accessCheckSwitch" name="accessCheckSwitch" class="field__control"
              data-test="access-check-switch" [(ngModel)]="otherSwitchKey">
              <option value="">(don't check a switch)</option>
              @for (featureSwitch of switchOptions; track featureSwitch.switchKey) {
                <option [value]="featureSwitch.switchKey">{{switchLabel(featureSwitch)}}</option>
              }
            </select>
          }
        </div>
        <div class="filter-bar__field">
          <label class="field__label" for="accessCheckRole">Role</label>
          <select id="accessCheckRole" name="accessCheckRole" class="field__control"
            data-test="access-check-role" [(ngModel)]="otherRole">
            <option value="">(don't check a role)</option>
            @for (role of roles; track role.id) {
              <option [value]="role.code">{{role.description}} ({{role.code}})</option>
            }
          </select>
        </div>
      }

      <div class="filter-bar__field tw:self-end">
        <button type="submit" class="btn btn-primary" data-test="access-check-submit" [disabled]="checking">
          {{checking ? 'Checking…' : 'Check'}}
        </button>
      </div>
    </form>

    <p class="tw:text-xs tw:text-gray-600" data-test="access-check-help">
      Choosing a page checks the gates that page actually uses: the feature behind its side-menu link and the page itself,
      and for an admin page the feature, role and switch its route needs.
    </p>

    @if (checkPage === otherPage) {
      <div class="tw:text-xs tw:text-gray-600 tw:mb-4" data-test="access-check-other-help">
        @if (switchListFailed) {
          <p data-test="access-check-switch-note">The switch list could not be loaded, so type the switch key instead.</p>
        }
        <p>Pick any combination of feature, switch and role. Each one you pick is checked for the user, and the others are skipped.</p>
        <ul>
          <li><strong>Feature</strong>: a role feature from Features by role. The user passes when any of their roles grants it.</li>
          <li><strong>Switch</strong>: a feature switch from New Features, checked for this user, including its targeting and stage.</li>
          <li><strong>Role</strong>: the user must hold that role, as System Controls needs ADMIN.</li>
        </ul>
        <p>The verdict passes only when every check you picked passes.</p>
      </div>
    }

    @if (checkError) {
      <p class="tw:text-red-700" role="alert" data-test="access-check-error">{{checkError}}</p>
    }

    @if (checkCandidates.length) {
      <fieldset class="tw:mb-4" data-test="access-check-candidates">
        <legend class="tw:font-bold">Several accounts use this email — choose one:</legend>
        @for (candidate of checkCandidates; track candidate.userId) {
          <label class="tw:block">
            <input type="radio" name="accessCheckCandidate" [value]="candidate.userId"
              [checked]="chosenUserId === candidate.userId" [disabled]="checking" (change)="chooseCandidate(candidate)" />
            {{candidate.userName}} — last sign-in {{candidate.lastSignInUtc ? (candidate.lastSignInUtc | date:'d MMM y, HH:mm') : 'never'}}
          </label>
        }
      </fieldset>
    }

    @if (checkResult) {
      <p class="tw:font-bold" role="status" data-test="access-check-verdict"
        [ngClass]="checkResult.allowed ? 'tw:text-green-700' : 'tw:text-red-700'">{{verdict}}</p>
      @if (checkResult.found && checkResult.roles?.length) {
        <p class="tw:text-xs tw:text-gray-600">Roles: {{checkResult.roles.join(', ')}}</p>
      }
      @if (checkResult.steps?.length) {
        <table class="admin-table admin-table--no-stack" data-test="access-check-steps">
          <thead>
            <tr>
              <th class="tw:text-left">Gate</th>
              <th class="tw:text-left">Name</th>
              <th class="tw:text-left">Result</th>
              <th class="tw:text-left">Detail</th>
            </tr>
          </thead>
          <tbody>
            @for (step of checkResult.steps; track $index) {
              <tr>
                <td>{{step.gate}}</td>
                <td>{{step.name}}</td>
                <td [ngClass]="step.passed ? 'tw:text-green-700' : 'tw:text-red-700'">{{step.passed ? 'Pass' : 'Fail'}}</td>
                <td>{{step.detail}}</td>
              </tr>
            }
          </tbody>
        </table>
      }
    }
  </section>

  <section class="admin-section">
    <h2 class="admin-h2">Features by role</h2>

    <!-- The shared filter-bar layout (filter-bar.css): label above a 38px control, the checkbox level with it. -->
    <div class="filter-bar tw:mb-4">
      <div class="filter-bar__field">
        <label class="field__label">Role</label>
        <app-searchable-select [(ngModel)]="selectedRoleId" [items]="roles"
          displayField="description" valueField="id"
          placeholder="Select a role"
          searchPlaceholder="Search roles…"
          (selectionChange)="loadFeatures()"></app-searchable-select>
      </div>

      <div class="filter-bar__field filter-bar__field--check">
        <label class="field__checkbox">
          <input type="checkbox" data-test="hide-unused" [checked]="hideUnused" (change)="hideUnused = $event.target.checked" />
          Hide unused
        </label>
      </div>
    </div>

    @if (loading) {
      <p>Loading…</p>
    }

    @if (!loading && selectedRoleId && hiddenCount > 0) {
      <p class="tw:text-xs tw:text-gray-500" data-test="hidden-count">{{hiddenCount}} unused {{hiddenCount === 1 ? 'feature' : 'features'}} hidden.</p>
    }

    <!-- As many columns as the width allows (at least 16rem each), so the list stays short on a wide screen. -->
    @if (!loading && selectedRoleId && visibleFeatures.length > 0) {
      <ul class="tw:grid tw:gap-2 tw:grid-cols-[repeat(auto-fill,minmax(16rem,1fr))] tw:list-none tw:p-0 tw:m-0" data-test="feature-grid">
        @for (feature of visibleFeatures; track feature.featureId) {
          <li class="tw:border tw:border-gray-200 tw:rounded tw:px-3 tw:py-2" data-test="feature-item"
            [ngClass]="{'tw:text-gray-500': isNotUsed(feature), 'tw:bg-green-50': feature.enabled}">
            <label class="tw:flex tw:items-start tw:gap-2 tw:m-0" [ngClass]="isLocked(feature) ? 'tw:cursor-not-allowed' : 'tw:cursor-pointer'">
              <input type="checkbox" class="tw:mt-1 tw:shrink-0" [checked]="feature.enabled" [disabled]="isLocked(feature)"
                [attr.data-feature]="feature.description" (change)="toggle(feature, $event.target.checked)" />
              <span class="tw:min-w-0" data-test="feature-text">
                <span class="tw:block tw:font-medium tw:break-words">{{feature.description}}</span>
                @if (isLocked(feature)) {
                  <span class="tw:block tw:text-xs tw:text-gray-600" data-test="admin-only">This is admin-only: it can be granted only to ADMIN.</span>
                }
                @if (feature.purpose) {
                  <span class="tw:block tw:text-xs" [ngClass]="isNotUsed(feature) ? 'tw:italic' : 'tw:text-gray-600'">{{feature.purpose}}</span>
                }
              </span>
            </label>
          </li>
        }
      </ul>
    }

    @if (!loading && selectedRoleId && features.length === 0) {
      <p>No features found.</p>
    }
  </section>

</div>
